import PropTypes from 'prop-types';
import React from 'react';
import { css } from 'emotion';
import AbstractForm from 'components/page/process/AbstractForm';
import Flexbox from 'flexbox-react';
import RIEComboBox from 'components/core/editable/RIEComboBox';
import RIEInput from 'components/core/editable/RIEInput';
import RIEUnits from 'components/core/editable/RIEUnits';
import InfoBoxLabel from 'components/core/InfoBoxLabel';
import ToolbarItem from 'components/core/toolbar/ToolbarItem';
import Toolbar from 'components/core/toolbar/Toolbar';
import Constants from 'utils/Constants';
import ComponentUtils from 'utils/ComponentUtils';
import ProcessUtils from 'utils/ProcessUtils';
import ProcessValidator from 'utils/ProcessValidator';
import VariableRequiredOptionsForm from 'components/core/forms/VariableRequiredOptionsForm';
import RetrievalRulesTable from 'components/page/process/variableRetrieval/inputs/RetrievalRulesTable';
import RetrievedVariableTransformation from 'components/core/fields/transformation_parameters/RetrievedVariableTransformation';
import CoordinateSystemField from 'components/core/fields/CoordinateSystemField';
import OutputVariableMappingField from 'components/core/fields/OutputVariableMappingField';
import VariableDimensionsField from 'components/core/fields/VariableDimensionsField';
import _ from 'lodash';

/**
 * The form that renders details for a retrieved variable
 */
export default class RetrievedVariableForm extends AbstractForm {
    static propTypes = {
        process: PropTypes.object,
        processErrors: PropTypes.object,
        id: PropTypes.string,
        hasWritePermissions: PropTypes.bool,
        onFieldUpdated: PropTypes.func
    };

    constructor(props) {
        super(props);
        this.validator = new ProcessValidator(props.process);
        this._initializePropDependentVariables(props);
    }

    componentWillUpdate(nextProps, nextState) {
        // Every time our props change, we need to refresh our coord sys, because the process has changed
        this._initializePropDependentVariables(nextProps);
    }

    shouldComponentUpdate(nextProps, nextState) {
        //componentWillReceiveProps is called twice when renaming, first time its called props.routeParams.entity has old name but
        //props.process has new name and so we were getting 'undefined' JS error in console. thats why we're checking for 'undefined' here
        if (nextProps.process.variable_retrieval.retrieved_variables[nextProps.match.params.entity]) {
            return true;
        }
        return false;
    }

    _initializePropDependentVariables(props) {
        const variableName = props.match.params.entity;
        this.retrievedVariable = props.process.variable_retrieval.retrieved_variables[variableName];
        this.coordinateSystem = this.props.process.variable_retrieval.coordinate_systems[this.retrievedVariable.coordinate_system];

        // base field path is relative to the process
        this.baseFieldPath = ['variable_retrieval', 'retrieved_variables', variableName];

        this.checkUnique = {
            label: 'retrieved variable',
            list: Object.keys(props.process.variable_retrieval.retrieved_variables)
        };

    }

    getDataType() {
        return Constants.getOptionById(this.retrievedVariable.data_type, Constants.DATA_TYPES);
    }

    getInputDataset() {
        return this.props.process.variable_retrieval.input_datasets[this.retrievedVariable.input_dataset];
    }

    onNameChanged = (fieldPath, value) => {
        const editor = ProcessUtils.getEditor(this.props.process);
        const oldName = this.retrievedVariable.name;
        const newName = value;
        editor.renameRetrievedVariable(oldName, newName, true);
        editor.commit();
    };

    onOutputDatastreamChanged = (fieldPath, newValue) => {
        const outputDatastreams = newValue.map(valueObj => {
            return valueObj.value;
        });
        const variableName = this.retrievedVariable.name;
        const editor = ProcessUtils.getEditor(this.props.process);
        editor.setVariableOutputDatastreams(variableName, outputDatastreams);
        editor.commit();
    };

    onCoordinateSystemChanged = (fieldPath, value) => {
        const coordinateSystemName = value.value;
        const editor = ProcessUtils.getEditor(this.props.process);
        const conflicts = editor.getRetrievedVariableCoordinateSystemConflicts(coordinateSystemName, [this.retrievedVariable.name]);

        if (_.isEmpty(conflicts)) {
            editor.addRetrievedVariablesToCoordinateSystem(coordinateSystemName, [this.retrievedVariable.name]);
            editor.commit();
        } else {
            ComponentUtils.promptDimensionConflictResolution(this.props.process,
                coordinateSystemName,
                conflicts,
                (resolutions) => {
                    editor.addRetrievedVariablesToCoordinateSystem(coordinateSystemName, [this.retrievedVariable.name], resolutions);
                    editor.commit();
                }
            );
        }
    };

    onOverrideChange = (fieldPath, value, onChangeData) => {
        const editor = ProcessUtils.getEditor(this.props.process);

        editor.updateTransformOverrideValueForVariables('transformation_type', [this.retrievedVariable.name], onChangeData.coordinateSystemName, onChangeData.dimensionName, value);
        editor.commit();
    };

    onOverrideDisplayChange = (fieldPath, value) => {
        const editor = ProcessUtils.getEditor(this.props.process);

        editor.updateTransformOverrideForVariables('transformation_type', [this.retrievedVariable.name], value);
        editor.commit();
    };

    onDeleteVariableClicked = () => {
        ComponentUtils.openDeleteEntitiesModal(this.props.process, 'variable_retrieval.retrieved_variables', this.retrievedVariable.name,
            this.validator.canRetrievedVariablesBeDeleted, true);
    };

    isOverrideTableVisible = () => {
        return ProcessUtils.variableHasTransformationOverride(this.props.process, 'transformation_type', this.retrievedVariable.name);
    };

    getOverrideTableData = () => {
        const coordinateSystemName = this.retrievedVariable.coordinate_system;
        const dimensions = {};

        this.retrievedVariable.dims.forEach(dimensionName => {
            const variableOverrideValue = ProcessUtils.getVariableTransformationTypeOverride(this.props.process, this.retrievedVariable.name, dimensionName);
            dimensions[dimensionName] = variableOverrideValue;
        });

        return { [coordinateSystemName]: dimensions };
    };

    render() {
        const inputDataset = this.getInputDataset();
        const outputDatastreams = Constants.convertArrayToSelectOptions(ProcessUtils.getRetrievedVariableOutputDatastreams(this.props.process, this.retrievedVariable.name));

        const labelWidth = '150px';
        const alignItems = 'center';
        return (
            <Flexbox className="retrieved-variable-form" flexDirection="column" flexGrow={1}>
                {/* Name */}
                <Flexbox flexDirection="row" marginBottom="10px" alignItems={alignItems}>
                    <InfoBoxLabel
                        labelWrap
                        className="noshrink"
                        label="Variable Name"
                        width={labelWidth}
                    />
                    <RIEInput
                        className="entity-name"
                        value={this.retrievedVariable.name}
                        change={this.onNameChanged}
                        fieldPath={[...this.baseFieldPath, 'name']}
                        isDisabled={!this.props.hasWritePermissions}
                        processId={this.props.process.name}
                        processErrors={this.props.processErrors}
                        urlCommit
                        checkUnique={{ list: Object.keys(this.props.process.variable_retrieval.retrieved_variables), label: 'retrieved variable' }}
                    />
                    <Toolbar>
                        <ToolbarItem
                            title="Delete variable"
                            className="fa fa-times delete"
                            clickHandler={this.onDeleteVariableClicked}
                            enabled={this.props.hasWritePermissions}
                            showText
                        />
                    </Toolbar>
                </Flexbox>
                <Flexbox flexDirection="row" alignItems="flex-start" className="noshrink">
                    <Flexbox flexDirection="column" marginRight="20px" minWidth="250px" className="noshrink">
                        {/* Type */}
                        <Flexbox flexDirection="row" marginBottom="10px" alignItems={alignItems}>
                            <InfoBoxLabel
                                labelWrap
                                className="noshrink"
                                label="Data Type"
                                width={labelWidth}
                            />
                            <RIEComboBox
                                value={this.getDataType()}
                                options={Constants.DATA_TYPES}
                                change={this.onFieldUpdated}
                                fieldPath={[...this.baseFieldPath, 'data_type']}
                                isDisabled={!this.props.hasWritePermissions}
                                processId={this.props.process.name}
                                processErrors={this.props.processErrors}
                            />
                        </Flexbox>
                        {/* Units */}
                        <Flexbox flexDirection="row" marginBottom="10px" alignItems={alignItems}>
                            <InfoBoxLabel
                                labelWrap
                                className="noshrink"
                                label="Units"
                                width={labelWidth}
                            />
                            <RIEUnits
                                value={ProcessUtils.getUnitsSelectValue(this.retrievedVariable.units)}
                                change={this.onFieldUpdated}
                                fieldPath={[...this.baseFieldPath, 'units']}
                                clearable={false}
                                options={Constants.RETRIEVED_VARIABLE_UNITS}
                                isDisabled={!this.props.hasWritePermissions}
                                processId={this.props.process.name}
                                processErrors={this.props.processErrors}
                            />
                        </Flexbox>
                        {/* Output Datastreams */}
                        <Flexbox flexDirection="row" marginBottom="10px" alignItems={alignItems}>
                            <InfoBoxLabel
                                labelWrap
                                className="noshrink"
                                label="Output Datastream(s)"
                                width={labelWidth}
                            />
                            <RIEComboBox
                                value={outputDatastreams}
                                options={ProcessUtils.getOutputDatstreamOptionsForVariable(this.props.process)}
                                change={this.onOutputDatastreamChanged}
                                isDisabled={!this.props.hasWritePermissions}
                                fieldPath={[...this.baseFieldPath, 'output_datastreams']}
                                minWidth="250px"
                                multi
                                clearable
                                creatable
                                urlCommit
                                processId={this.props.process.name}
                                processErrors={this.props.processErrors}
                            />
                        </Flexbox>
                        {/* Output variable Mapping */}
                        {outputDatastreams.length > 0 &&
                        <Flexbox flexDirection="row" marginBottom="10px" alignItems={alignItems}>
                            <OutputVariableMappingField
                                process={this.props.process}
                                processErrors={this.props.processErrors}
                                hasWritePermissions={this.props.hasWritePermissions}
                                retrievedVariableName={this.retrievedVariable.name}
                                labelWidth={labelWidth}
                                displayLabel
                            />
                        </Flexbox>
                        }
                        {/* Coord System */}
                        <Flexbox flexDirection="row" marginBottom="20px" alignItems={alignItems}>
                            <CoordinateSystemField
                                process={this.props.process}
                                processErrors={this.props.processErrors}
                                hasWritePermissions={this.props.hasWritePermissions}
                                retrievedVariableName={this.retrievedVariable.name}
                                labelWidth={labelWidth}
                                displayLabel
                                className={css`max-width:700px`}
                            />
                        </Flexbox>

                        {/* Variable Dimensions */}
                        {this.retrievedVariable.dims.length > 0 &&
                        <Flexbox flexDirection="row" marginBottom="20px" alignItems={alignItems}>
                            <VariableDimensionsField
                                process={this.props.process}
                                processErrors={this.props.processErrors}
                                hasWritePermissions={this.props.hasWritePermissions}
                                retrievedVariableName={this.retrievedVariable.name}
                                displayLabel
                                labelWidth={labelWidth}
                            />
                        </Flexbox>
                        }

                        {/* Advanced Transformation */}
                        <RetrievedVariableTransformation
                            process={this.props.process}
                            processErrors={this.props.processErrors}
                            retrievedVariableNames={[this.retrievedVariable.name]}
                            isOverrideTableVisible={this.isOverrideTableVisible()}
                            onOverrideChange={this.onOverrideChange}
                            onOverrideDisplayChange={this.onOverrideDisplayChange}
                            overrideTableData={this.getOverrideTableData()}
                        />
                    </Flexbox>

                    {/* yes/no questions */}
                    <Flexbox className="yes-no-box" flexDirection="column" marginLeft="5px">
                        <VariableRequiredOptionsForm
                            process={this.props.process}
                            retrievedVariableName={this.retrievedVariable.name}
                            isDisabled={!this.props.hasWritePermissions}
                            variableRequired={this.retrievedVariable.variable_required}
                            retrieveQCVariable={this.retrievedVariable.retrieve_qc_variable}
                            qcVariableRequired={this.retrievedVariable.qc_variable_required}
                            variableRequiredText="Required?"
                            retrieveQCVariableText="Retrieve QC?"
                            qcVariableRequiredText="QC Required?"
                            inlineStyle
                        />
                    </Flexbox>
                </Flexbox>
                <Flexbox flexDirection="row" flexGrow={1} paddingTop="5px">
                    <RetrievalRulesTable
                        className="retrieval-rules-table"
                        process={this.props.process}
                        inputDatasetNames={[inputDataset.name]}
                        highlightVariableName={this.retrievedVariable.name}
                        dods={this.props.dods}
                        dodsById={this.props.dodsById}
                        siteFacilities={this.props.siteFacilities}
                        hasWritePermissions={this.props.hasWritePermissions}
                        processErrors={this.props.processErrors}
                        hideToolbar
                        selectable={false}
                    />
                </Flexbox>
            </Flexbox>
        );
    }
}




// WEBPACK FOOTER //
// ./src/components/page/process/variableRetrieval/retrievedVariable/RetrievedVariableForm.js